import React from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  TimeScale,
  ChartOptions,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import 'chartjs-adapter-date-fns';
import { de } from 'date-fns/locale';
import { DayAheadData, IntradayData } from '../types/price';
import './PriceChart.css';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  TimeScale
);

interface PriceChartProps {
  dayAheadData?: DayAheadData;
  intradayData?: IntradayData | null;
  title: string;
  isBrutto?: boolean;
  convertPrice?: (price: number) => number;
  showCurrentTime?: boolean;
}

const PriceChart: React.FC<PriceChartProps> = ({
  dayAheadData,
  intradayData,
  title,
  isBrutto = false,
  convertPrice = (price) => price,
  showCurrentTime = false,
}) => {
  const formatDate = (dateString: string) => {
    return new Date(dateString);
  };

  // Calculate average price with conversion
  const calculateAverage = (prices: Array<{timestamp: string, price: number}>) => {
    if (!prices || prices.length === 0) return 0;
    const sum = prices.reduce((acc, p) => acc + convertPrice(p.price), 0);
    return sum / prices.length;
  };

  const options: ChartOptions<'line'> = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: 'index' as const,
      intersect: false,
    },
    plugins: {
      legend: {
        position: 'top' as const,
        labels: {
          usePointStyle: true,
          padding: 20,
          font: {
            size: 14,
          },
        },
      },
      title: {
        display: true,
        text: title,
        font: {
          size: 18,
          weight: 'bold',
        },
        padding: {
          bottom: 20,
        },
      },
      tooltip: {
        mode: 'index',
        intersect: false,
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        titleFont: {
          size: 14,
        },
        bodyFont: {
          size: 13,
        },
        padding: 12,
        cornerRadius: 8,
        callbacks: {
          title: (context) => {
            const date = new Date(context[0].parsed.x);
            return date.toLocaleString('de-DE', {
              day: '2-digit',
              month: '2-digit',
              year: 'numeric',
              hour: '2-digit',
              minute: '2-digit',
              hour12: false
            });
          },
          label: (context) => {
            let label = context.dataset.label || '';
            if (label) {
              label += ': ';
            }
            if (context.parsed.y !== null) {
              label += (context.parsed.y / 10).toLocaleString('de-DE', { 
                minimumFractionDigits: 2, 
                maximumFractionDigits: 2 
              }) + ' ct/kWh';
            }
            return label;
          },
        },
      },
    },
    scales: {
      x: {
        type: 'time',
        adapters: {
          date: {
            locale: de,
          },
        },
        time: {
          displayFormats: {
            hour: 'HH:mm',
            day: 'dd.MM',
          },
        },
        grid: {
          display: false,
        },
        ticks: {
          font: {
            size: 12,
          },
        },
      },
      y: {
        beginAtZero: false,
        grid: {
          color: 'rgba(0, 0, 0, 0.05)',
        },
        ticks: {
          font: {
            size: 12,
          },
          callback: function(value) {
            return ((value as number) / 10).toLocaleString('de-DE', { 
              minimumFractionDigits: 1, 
              maximumFractionDigits: 1 
            }) + ' ct';
          },
        },
      },
    },
    elements: {
      line: {
        tension: 0, // Disable bezier curves
        borderWidth: 3,
      },
      point: {
        radius: 0,
        hoverRadius: 6,
        hitRadius: 30,
        hoverBorderWidth: 2,
      },
    },
  };

  const datasets = [];

  // Prefer intraday data if available (more granular), otherwise use day-ahead
  const primaryData = intradayData?.prices || dayAheadData?.prices;
  const dataLabel = intradayData?.prices ? 'Intraday Preise (15-Minuten)' : 'Day-Ahead Preise (Stündlich)';

  if (primaryData) {
    const average = calculateAverage(primaryData);

    // Calculate min and max converted values for better color distribution
    const convertedPrices = primaryData.map(p => convertPrice(p.price));
    const minPrice = Math.min(...convertedPrices);
    const maxPrice = Math.max(...convertedPrices);

    // Create gradient for the chart
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (ctx) {
      const gradient = ctx.createLinearGradient(0, 0, 0, 400);
      gradient.addColorStop(0, 'rgba(255, 107, 0, 0.8)');   // Orange at top
      gradient.addColorStop(0.5, 'rgba(255, 193, 7, 0.8)'); // Yellow in middle
      gradient.addColorStop(1, 'rgba(76, 175, 80, 0.8)');   // Green at bottom

      const fillGradient = ctx.createLinearGradient(0, 0, 0, 400);
      fillGradient.addColorStop(0, 'rgba(255, 107, 0, 0.1)');   // Orange at top
      fillGradient.addColorStop(0.5, 'rgba(255, 193, 7, 0.1)'); // Yellow in middle
      fillGradient.addColorStop(1, 'rgba(76, 175, 80, 0.1)');   // Green at bottom

      datasets.push({
        label: dataLabel,
        stepped: intradayData?.prices ? true : false,
        data: primaryData.map(p => ({
          x: formatDate(p.timestamp),
          y: convertPrice(p.price),
        })),
        segment: {
          borderColor: (ctx: any) => {
            const y0 = ctx.p0.parsed.y;
            const y1 = ctx.p1.parsed.y;
            const avgY = (y0 + y1) / 2;

            // Calculate normalized position (0 to 1) based on min/max range
            const normalizedValue = (avgY - minPrice) / (maxPrice - minPrice);

            // Clamp to [0, 1] range
            const clampedValue = Math.max(0, Math.min(1, normalizedValue));

            let r, g, b;
            if (clampedValue <= 0.5) {
              // Green to Yellow transition (low to medium prices)
              const t = clampedValue * 2; // 0 to 1
              const eased = t * t * (3 - 2 * t); // Smoothstep function
              r = Math.round(76 + (255 - 76) * eased);   // 76 -> 255
              g = Math.round(175 + (193 - 175) * eased); // 175 -> 193
              b = Math.round(80 - (80 - 7) * eased);     // 80 -> 7
            } else {
              // Yellow to Orange transition (medium to high prices)
              const t = (clampedValue - 0.5) * 2; // 0 to 1
              const eased = t * t * (3 - 2 * t); // Smoothstep function
              r = 255;                                    // stays 255
              g = Math.round(193 - (193 - 107) * eased); // 193 -> 107
              b = Math.round(7 - 7 * eased);             // 7 -> 0
            }

            return `rgba(${r}, ${g}, ${b}, 0.8)`;
          },
        },
        borderWidth: 3,
        backgroundColor: fillGradient,
        fill: true,
        pointBackgroundColor: (ctx: any) => {
          const yValue = ctx.parsed.y;

          // Calculate normalized position (0 to 1) based on min/max range
          const normalizedValue = (yValue - minPrice) / (maxPrice - minPrice);

          // Clamp to [0, 1] range
          const clampedValue = Math.max(0, Math.min(1, normalizedValue));

          let r, g, b;
          if (clampedValue <= 0.5) {
            // Green to Yellow transition (low to medium prices)
            const t = clampedValue * 2; // 0 to 1
            const eased = t * t * (3 - 2 * t); // Smoothstep function
            r = Math.round(76 + (255 - 76) * eased);   // 76 -> 255
            g = Math.round(175 + (193 - 175) * eased); // 175 -> 193
            b = Math.round(80 - (80 - 7) * eased);     // 80 -> 7
          } else {
            // Yellow to Orange transition (medium to high prices)
            const t = (clampedValue - 0.5) * 2; // 0 to 1
            const eased = t * t * (3 - 2 * t); // Smoothstep function
            r = 255;                                    // stays 255
            g = Math.round(193 - (193 - 107) * eased); // 193 -> 107
            b = Math.round(7 - 7 * eased);             // 7 -> 0
          }

          return `rgba(${r}, ${g}, ${b}, 0.8)`;
        },
      });

      // Add average line
      datasets.push({
        label: `Tagesdurchschnitt (${(average / 10).toLocaleString('de-DE', { 
          minimumFractionDigits: 2, 
          maximumFractionDigits: 2 
        })} ct/kWh)`,
        data: primaryData.map(p => ({
          x: formatDate(p.timestamp),
          y: average,
        })),
        borderColor: 'rgba(150, 150, 150, 0.5)',
        borderWidth: 2,
        borderDash: [5, 5],
        fill: false,
        pointRadius: 0,
        pointHoverRadius: 0,
      });

      // Add current time line if showing current time
      if (showCurrentTime) {
        const now = new Date();
        const currentTimeData = primaryData.map(p => ({
          x: formatDate(p.timestamp),
          y: null, // Will be ignored, we just need the time range
        }));

        datasets.push({
          label: 'Aktuelle Zeit',
          data: [
            { x: now, y: minPrice },
            { x: now, y: maxPrice }
          ],
          borderColor: 'rgba(255, 69, 0, 0.8)',
          backgroundColor: 'rgba(255, 69, 0, 0.1)',
          borderWidth: 2,
          fill: false,
          pointRadius: 0,
          pointHoverRadius: 0,
          showLine: true,
        });
      }
    }
  }

  const data = {
    datasets,
  };

  return (
    <div className="price-chart-container">
      <Line options={options} data={data} />
    </div>
  );
};

export default PriceChart;